import { supabase } from './supabaseClient.js';

/**
 * Gamification System
 *
 * Tracks XP, levels, and daily login streaks.
 * XP is earned from various activities; levels are derived from XP.
 *
 * XP rewards:
 *   - Daily login:     10 XP
 *   - Send DM:          5 XP
 *   - Send channel msg:  3 XP
 *   - Start story:     15 XP
 *   - Phone call:      10 XP
 *   - Streak bonus:     streak_days * 2 XP (on login)
 */

// ─── LEVEL THRESHOLDS ─────────────────────────────────────────────────────
// Level N requires LEVEL_XP[N] cumulative XP
const LEVEL_XP = [
  0,      // Level 1: 0 XP
  50,     // Level 2: 50 XP
  150,    // Level 3
  300,    // Level 4
  500,    // Level 5
  800,    // Level 6
  1200,   // Level 7
  1700,   // Level 8
  2400,   // Level 9
  3200,   // Level 10
  4200,   // Level 11
  5500,   // Level 12
  7000,   // Level 13
  9000,   // Level 14
  11500,  // Level 15
];

export function getLevelForXP(xp) {
  for (let i = LEVEL_XP.length - 1; i >= 0; i--) {
    if (xp >= LEVEL_XP[i]) return i + 1;
  }
  return 1;
}

export function getXPForNextLevel(currentXP) {
  const level = getLevelForXP(currentXP);
  if (level >= LEVEL_XP.length) return { needed: 0, total: 0, progress: 1 }; // Max level
  const currentThreshold = LEVEL_XP[level - 1];
  const nextThreshold = LEVEL_XP[level];
  return {
    needed: nextThreshold - currentXP,
    total: nextThreshold - currentThreshold,
    progress: (currentXP - currentThreshold) / (nextThreshold - currentThreshold),
  };
}

export function getLevelTitle(level) {
  const titles = [
    'Newcomer',        // 1
    'Curious Soul',    // 2
    'Story Seeker',    // 3
    'Fate Touched',    // 4
    'Devoted Reader',  // 5
    'Lore Keeper',     // 6
    'Heart Bound',     // 7
    'Fate Weaver',     // 8
    'Soul Entwined',   // 9
    'Destiny Forged',  // 10
    'Myth Walker',     // 11
    'Eternal Flame',   // 12
    'Legend Born',      // 13
    'Fate Master',     // 14
    'Ascended',        // 15
  ];
  return titles[Math.min(level - 1, titles.length - 1)] || 'Ascended';
}

// ─── XP REWARDS ───────────────────────────────────────────────────────────
export const XP_REWARDS = {
  daily_login: 10,
  send_dm: 5,
  send_channel_msg: 3,
  start_story: 15,
  phone_call: 10,
  daily_fate_card: 15,
};

// ─── RECORD DAILY LOGIN + STREAK ──────────────────────────────────────────
export async function recordDailyLogin(userId) {
  if (!userId) return null;

  try {
    // Calendar dates in the players' timezone (ET), not UTC. The UTC date
    // flips at 7-8pm New Jersey time, so an evening login + a later-that-night
    // login counted as TWO days (double streak increment), and the real next
    // morning could look like a skipped day.
    const etDay = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD
    const today = etDay(new Date());
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = etDay(yesterday);

    // Atomic in Postgres: the RPC reads the row FOR UPDATE, so a concurrent
    // award_xp (e.g. the first message sent right after open) serializes behind
    // it. The old client select-then-update could lose the login+streak bonus to
    // a last-write-wins clobber.
    const { data, error } = await supabase.rpc('record_daily_login', {
      p_user: userId, p_today: today, p_yesterday: yesterdayStr,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    if (row.already_logged) {
      return { alreadyLogged: true, newXP: row.new_xp, newLevel: row.new_level, newStreak: row.new_streak, newLongest: row.new_longest };
    }
    return {
      alreadyLogged: false,
      xpGain: row.xp_gain,
      streakBonus: row.streak_bonus,
      newStreak: row.new_streak,
      newXP: row.new_xp,
      newLevel: row.new_level,
      leveledUp: row.leveled_up,
      newLongest: row.new_longest,
    };
  } catch (err) {
    console.error('[Gamification] Error:', err);
    return null;
  }
}

// ─── AWARD XP FOR ACTIVITY ────────────────────────────────────────────────
export async function awardXP(userId, activity, amountOverride) {
  if (!userId || (!XP_REWARDS[activity] && !amountOverride)) return null;

  try {
    const xpGain = amountOverride ?? XP_REWARDS[activity];

    // Read prior level ONLY to report leveledUp (cosmetic). The XP mutation is
    // done atomically in Postgres (award_xp RPC = single UPDATE xp = xp + n) so
    // two overlapping awards can no longer last-write-wins clobber each other.
    const { data: prior } = await supabase
      .from('profiles')
      .select('level')
      .eq('id', userId)
      .single();

    const { data, error } = await supabase.rpc('award_xp', { p_user: userId, p_amount: xpGain });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) return null;

    return {
      xpGain,
      newXP: row.xp,
      newLevel: row.level,
      leveledUp: row.level > (prior?.level || 1),
    };
  } catch (err) {
    console.error('[Gamification] Award XP error:', err);
    return null;
  }
}
